/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider, FormControl, InputLabel, TextField } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Autocomplete } from "@material-ui/lab";
import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";
import Dialog from "../dialog/functional/Dialog";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import AutoForm from "../form/functional/AutoForm";
import DialogHeaderWithClose from "../dialog/functional/DialogHeaderWithClose";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px"
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text
    },
}));

/**
 * @COMPONENT_NAME MachineStateCodeUpdateDialog
 * @DESCRIPTION 설비 유형 코드에 따른 설비 상태 데이터를 받아서 커스텀 AutoComplete을 만들어 사용자가 해당 설비 상태를 선택할 수 있음
 *              다이어로그 안에 AutoForm을 포함시키며 api 호출 및 api 호출에 따른 알림을 수행함
 *id, schema, , moveable, resizable, fullWidth, maxWidth, onClose,
        onInitialized, initialData, onSuccess, modifyUrlCallback
 * @PROPS
 *    - {string} id - 다이얼로그 ID
 *    - {object} schema - AutoForm에 전달될 스키마 정보
 *    - {boolean} moveable - 다이얼로그 이동 가능 여부
 *    - {boolean} resizable - 다이얼로그 크기 조절 가능 여부
 *    - {boolean} fullWidth - 다이얼로그 전체 너비 사용 여부
 *    - {string} maxWidth - 다이얼로그 최대 너비
 *    - {Function} onInitialized - 다이얼로그 초기화 후 호출되는 콜백
 *    - {object} initialData - 폼 초기 데이터
 *    - {Function} onSuccess - API 호출 성공 시 실행할 콜백
 *    - {Function} onClose - 다이얼로그 닫기 시 실행할 콜백
 *    - {Function} modifyUrlCallback - API URL을 동적으로 수정하는 함수
 *
 * @PASSTHROUGH_PROPS
 *    Dialog 컴포넌트 props
 *    - {id} - 다이얼로그 ID
 *    - {movable} - 다이얼로그 이동 가능 여부
 *    - {boolean} resizable - 다이얼로그 크기 조절 가능 여부
 *    - {boolean} fullWidth - 다이얼로그 전체 너비 사용 여부
 *    - {string} lg - 다이얼로그 최대 너비
 *    - {onCloseDialog} - 다이얼로그 닫을 때 호출되는 콜백 
 *    - {onDialogInitialized} - 다이얼로그 초기화 시 호출되는 콜백
 *    Autocomplete 컴포넌트 props
 *    - {object} machineStateCodes - AutoComplete 컴포넌트 내부에서 사용되는 options 목록 object
 *    - {Function} (option) => option.label - 선택한 데이터의 label 데이터를 반환하는 콜백함수
 *    - {object} machineStateObj - 선택한 데이터 object
 *    - {Function} onStateChange - select 변경 시 호출퇴는 콜백 함수
 *    
 *
 * @CALLBACKS
 *    - {Function} onInitialized - 다이얼로그가 초기화될 때 호출됨
 *    - {Function} onApiSuccess - API 요청 성공 후 결과를 전달받고 formData 변경 및 notifySuccess에 전달
 *    - {Function} onApiFailure - API 요청 실패 후 결과를 notifyFailure에 전달
 *    - {Function} onClose - 다이얼로그를 닫을 때 호출됨
 *    - {Function} customValidation - 커스텀 유효성 검사를 수행함 (true 반환 시 유효성 실패로 간주)
 *    - {Function} modifyUrlCallback - API 요청 URL을 동적으로 변경함
 *    - {Function} nofifyWarning - 경고 알림
 *    - {Function} nofifySuccess - 성공 알림
 *    - {Function} nofifyFailure - 실패 알림
 *    - {Function} onStateChange - select 데이터 변경 시 machineStateObj, machineStateCode, machineStateName 상태 변경 콜백함수
 *    
 *
 * @EXAMPLES
 * ```jsx

 * <Dialog
            id={id}
            moveable={moveable}
            resizable={resizable}
            fullWidth={fullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
        
        <AutoForm
            id="pop-production-form"
            schema={schema}
            mode="edit"
            initialData={formData}
            onChange={onAutoFormChange}
        />

        {machineStateCodes ? (
            <FormControl style={{ margin: "1%", width: "50%" }}>
                <Autocomplete
                    id="MachineState-select"
                    style={{ paddingTop: "16px" }}
                    options={[{label:"준비중", value: "state-1"}, {label:"작동중", value: "state-2"}]}
                    getOptionLabel={(option) => option.label}
                    value={{label:"준비중", value: "state-1"}}
                    onChange={onStateChange}
                    renderInput={(params) => <TextField {...params} />}
                />
            </FormControl>
        ) : (
            <></>
        )}
    </Dialog>
 * ```
 */

function MachineStateCodeUpdateDialog(props) {
    const {
        id, schema, moveable, resizable, fullWidth, maxWidth, onClose,
        onInitialized, initialData, onSuccess, modifyUrlCallback, ...others
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    const [instance, setInstance] = useState();
    const [keepOpen, setKeepOpen] = useState(false);
    const [formData, setFormData] = useState();
    const [machineStateCodes, setMachineStateCodes] = useState();
    const [machineStateObj, setMachineStateObj] = useState({ label: initialData?.machineStateName, value: initialData?.machineStateCode });
    const [disabled, setDisabled] = useState(true);
    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    useEffect(() => {

        setFormData(() => {
            return { ...initialData, machineStateCode: machineStateObj?.value, machineStateName: machineStateObj?.label };
        });

    }, [machineStateObj]);


    useEffect(() => {
        let mounted = true;

        (async () => {
            let result = await apiClient.get(`api/VwMachineTypeState/${initialData?.machineTypeCode}`);

            let machineStateCodes = result.data.data.map((data) => {
                return { label: data["machineStateName"], value: data["machineStateCode"] }
            });

            if (mounted) {
                setMachineStateCodes(machineStateCodes);
                if (initialData?.machineStateCode) {
                    const matched = machineStateCodes.find(
                        option => option.value === initialData?.machineStateCode
                    );
                    if (matched) {
                        setMachineStateObj(matched);
                    }
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, [initialData]);

    const checkItemValidation = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("create", column, column.validation, formData[column.name] || "");
            if (!valid) {
                nofifyWarning(msg);
                return false;
            }
        }
        return true;
    }

    const nofifyWarning = (msg) => {
        console.warn(msg);
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        console.error(msg);
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const onDialogInitialized = (instance) => {
        setInstance(instance);
        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onApiSuccess = (result) => {
        setFormData((formData) => {
            let newFormData = {
                ...formData,
            }
            return newFormData;
        });

        setKeepOpen((keepOpen) => {
            setDisabled(true);
            if (!keepOpen) {
                instance.hide();
            }
            return keepOpen;
        });

        if (onSuccess) {
            onSuccess(result.data);
        }
    };

    const onApiFailure = (reason) => {
        notifyFailure(t(reason.response.data));
    };

    const onOk = () => {

        save(schema);
    }

    function save(schema) {
        if (!checkItemValidation(schema, formData)) {
            console.error("Form Validation Failed");
            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(
                formData,
                schema.validation
            );

            if (!valid) {
                nofifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        // build base api url
        let url = `/api/MomSysMstMachine/machineStateCode`;

        if (modifyUrlCallback) {
            url = modifyUrlCallback(url);
        }

        let apiData = { ...formData };
        let form = new FormData();
        let json = {};
        let deleted = {};

        //autoFormDialog columnMap 생성부분 
        if (schema) {
            let columnMap = {};
            schema.columns.map(column => columnMap[column.name] = column);
            schema.columnMap = columnMap;
        }

        const keys = Object.keys(apiData);

        // 파일정보가 아닌경우 json 추가
        const notFileKeys = keys.filter(key => notFileKey(schema.columnMap, key));
        notFileKeys
            .filter(key => notUndefined(apiData[key], key))
            .forEach(key => json[key] = apiData[key]);

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .put(url, form, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    }

    function notFileKey(columnMap, key) {
        if (columnMap && columnMap[key] && columnMap[key].formItemType !== "file") {
            return key;
        }
    }

    function notUndefined(apiData, key) {
        if (apiData !== undefined) {
            return key;
        }
    }

    const onCancel = () => {
        setFormData((formData) => {
            let newFormData = {
                ...formData,
            }
            return newFormData;
        });
        instance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => (!keepOpen))}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </>
        )
    }

    const onCloseNotification = () => {
        setNotification((notification) => {
            return ({ ...notification, open: false });
        });
    };

    const onCloseDialog = () => {
        setDisabled(true);
        setMachineStateObj(null);
        setFormData((formData) => {
            let newFormData = {
                ...formData
            }
            return newFormData;
        });

        if (onClose) {
            onClose();
        }
    }
    const onStateChange = (e, option) => {
        setDisabled(false)
        setMachineStateObj(option);
    };

    return (
        <Dialog
            id={id}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeaderWithClose>
                <DialogTitle>
                    {t(`dialog.title.MomSysMachineStateCodeMgmt.update`)}
                </DialogTitle>
            </DialogHeaderWithClose>
            <Divider />

            <DialogContent>
                <div
                    style={{
                        padding: "16px"
                    }}
                >
                    <AutoForm
                        id="machineStateCodeUpdate-form"
                        schema={schema}
                        mode="edit"
                        initialData={formData}
                    />

                    {machineStateCodes ? (
                        <FormControl style={{ margin: "1%", width: "50%" }}>
                            <InputLabel id={id + "-label"} shrink={true} style={{ fontSize: "large", fontWeight: "bold" }}>
                                {t("columnName.machineStateName")}
                            </InputLabel>
                            <Autocomplete
                                id="MachineState-select"
                                style={{ paddingTop: "16px" }}
                                options={machineStateCodes}
                                getOptionLabel={(option) => option.label}
                                value={machineStateObj}
                                onChange={onStateChange}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </FormControl>
                    ) : (
                        <></>
                    )}
                </div>
                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert
                        onClose={onCloseNotification}
                        variant="filled"
                        severity={notification.severity}
                    >
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>
            <Divider />
            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                labels={[t("common.add")]}
                buttonDisabled={[disabled, false]}
                onOk={() => onOk(schema, initialData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default MachineStateCodeUpdateDialog;
